/**
 * RTK's createAsyncThunk serializes a falsy thrown/rejected value as `{ message: "Rejected" }`,
 * which shows up in Datadog RUM as an opaque `Error: Rejected` with no useful context.
 * Always rethrow a real Error (or rejectWithValue a real payload) instead.
 */

export const toError = (error: unknown, fallback = 'Request failed'): Error => {
    if (error instanceof Error) {
        return error;
    }
    if (typeof error === 'string' && error) {
        return new Error(error);
    }
    if (error && typeof error === 'object') {
        const maybe = error as { message?: unknown; data?: { message?: unknown } };
        const message = maybe.message || maybe.data?.message;
        if (typeof message === 'string' && message) {
            return new Error(message);
        }
    }
    return new Error(fallback);
};

/** For createAsyncThunk / React Query catch blocks that previously did `throw error.message`. */
export const rethrowAsError = (error: unknown, fallback = 'Request failed'): never => {
    throw toError(error, fallback);
};

/** Payload for `rejectWithValue` that is never undefined/null (those become RTK "Rejected"). */
export const rejectPayloadFromError = (error: any, fallback = 'Request failed') => {
    if (error?.data !== undefined && error?.data !== null) {
        return error.data;
    }
    return {
        message: (typeof error?.message === 'string' && error.message) || fallback,
        code: error?.code,
    };
};
